import { QueryClient } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import defaultChatAvatarUrl from '../assets/chat_avatar.png';
import defaultHeaderAvatarUrl from '../assets/your_company.png';
import { SnackbarContextType } from '../contexes/SnackBarProvider';
import { ChatAgent, ChatAgentFormData } from '../interfaces/ChatAgent';
import Knowledge, { KnowledgeFileData } from '../interfaces/Knowledge';
import User from '../interfaces/User';
import { updateChatAgent } from '../services/chatAgentService';
import { TranslationKey } from '../types/i18n';
import { resolveEditAvatarUrl } from './chatAgentAvatarUpload';
import {
  createNewKnowledge,
  getOrCreateKnowledgeId,
  saveWebscrapConfig,
  uploadKnowledgeFiles,
} from './knowledgeHelper';

export interface UpdateChatAgentSubmitContext {
  methods: UseFormReturn<ChatAgentFormData>;
  chatAgent: ChatAgent;
  currentUser: User | null;
  knowledge?: Knowledge;
  knowledgeFilesData: KnowledgeFileData[];
  executeStagedDeletions: () => Promise<{ failed: string[] }>;
  queryClient: QueryClient;
  displayMessage: SnackbarContextType;
  t: (key: TranslationKey, options?: Record<string, unknown>) => string;
  setIsAgentCreated: (value: boolean) => void;
  setIsKnowledgeCreated: (value: boolean) => void;
  setIsFilesUploaded: (value: boolean) => void;
}

// Handles the EDIT-workflow branch of ChatAgentForm's submit: resolves the
// final avatar URLs, builds the update payload from only the fields that
// changed, updates the chat agent, uploads any newly-staged knowledge files
// (unless the user just switched to a different existing knowledge base),
// and flushes staged deletions. Throws on failure — the caller's shared
// try/catch handles displaying the error and resetting isProcessing. See
// docs/specs/agent-form-click-to-jump-navigation.md.
export const submitUpdateChatAgent = async (
  formData: ChatAgentFormData,
  ctx: UpdateChatAgentSubmitContext
): Promise<void> => {
  const {
    methods,
    chatAgent,
    currentUser,
    knowledge,
    knowledgeFilesData,
    executeStagedDeletions,
    queryClient,
    displayMessage,
    t,
    setIsAgentCreated,
    setIsKnowledgeCreated,
    setIsFilesUploaded,
  } = ctx;
  const { dirtyFields } = methods.formState;

  // Resolve the final URL for each avatar field: the newly-uploaded
  // image if the user staged one, the existing URL if untouched, or
  // the app's default asset as a last resort.
  const newHeaderAvatarUrl = await resolveEditAvatarUrl({
    isDirty: !!dirtyFields.header_avatar_url,
    dataUrl: formData.header_avatar_url,
    fileName: formData.header_avatar_url_filename,
    defaultAssetUrl: defaultHeaderAvatarUrl,
    defaultAssetFileName: 'your_company.png',
    userId: currentUser!.id,
    agentId: chatAgent.id,
  });

  const newChatAvatarUrl = await resolveEditAvatarUrl({
    isDirty: !!dirtyFields.chat_avatar_url,
    dataUrl: formData.chat_avatar_url,
    fileName: formData.chat_avatar_url_filename,
    defaultAssetUrl: defaultChatAvatarUrl,
    defaultAssetFileName: 'chat_avatar.png',
    userId: currentUser!.id,
    agentId: chatAgent.id,
  });

  // Build update data with only valid fields for the API
  const updateData: Record<string, unknown> = {};

  // Root-level fields
  if (formData.analytics_categories)
    updateData.analytics_categories = formData.analytics_categories;
  if (formData.analytics_language)
    updateData.analytics_language = formData.analytics_language;
  // Where newly staged files get uploaded: none when detaching or when
  // switching to a different existing knowledge base (staged files were
  // meant for the previously attached one).
  let knowledgeForFiles: Knowledge | undefined = knowledge;
  if (formData.knowledge_mode === 'none') {
    // Detach only — the knowledge base itself is never deleted, since other
    // agents may still use it.
    updateData.knowledge_ids = [];
    knowledgeForFiles = undefined;
  } else if (formData.existing_knowledge_id) {
    // User switched to a different existing knowledge base via the
    // toggle — detach and reference it instead of mutating the
    // previously attached one, which other agents may still use.
    updateData.knowledge_ids = [formData.existing_knowledge_id];
    knowledgeForFiles = undefined;
  } else if (!knowledge) {
    // The agent has no knowledge base yet: create one and attach it.
    const newKnowledgeId = await getOrCreateKnowledgeId(undefined, () =>
      createNewKnowledge(
        formData.knowledge_name,
        currentUser!.id,
        currentUser!.company_id,
        formData.knowledge_language
      )
    );
    updateData.knowledge_ids = [newKnowledgeId];
    knowledgeForFiles = { id: newKnowledgeId } as Knowledge;
  } else if (formData.knowledge_ids) {
    updateData.knowledge_ids = formData.knowledge_ids;
  }

  // Custom initialization fields. theme_color/header_text use
  // `!== undefined` (like user_data_prompt_html_disclaimer/start_message
  // below), not truthiness — RHF never actually yields undefined for them
  // post-reset (defaultValues/reset both fall back to ''), so this always
  // includes their current value, meaning a cleared field is sent as an
  // explicit '' instead of being silently omitted and leaving the stale
  // server value in place. The backend patches each of these individually
  // (/custom_initialization/<field>), so this can't clobber a sibling
  // field.
  if (formData.theme_color !== undefined)
    updateData.theme_color = formData.theme_color;
  if (formData.size) updateData.size = formData.size;
  if (formData.user_data_prompt)
    updateData.user_data_prompt = formData.user_data_prompt;
  if (formData.user_data_prompt_html_disclaimer !== undefined)
    updateData.user_data_prompt_html_disclaimer =
      formData.user_data_prompt_html_disclaimer;
  if (formData.header_text !== undefined)
    updateData.header_text = formData.header_text;
  if (formData.prompt_suggestions)
    updateData.prompt_suggestions = formData.prompt_suggestions;

  // Labels to translate fields (sent at root level, backend nests them
  // under custom_initialization.labels_to_translate — as a single object,
  // not patched key-by-key like the fields above). All three must use
  // `!== undefined` (never actually undefined post-reset) so they're
  // always sent together as a complete set: the backend replaces
  // labels_to_translate wholesale from whichever of these three keys are
  // present in the request, so omitting one that's merely empty here would
  // silently drop it from the stored object instead of clearing it.
  if (formData.user_data_prompt_title !== undefined)
    updateData.user_data_prompt_title = formData.user_data_prompt_title;
  if (formData.user_data_prompt_button !== undefined)
    updateData.user_data_prompt_button = formData.user_data_prompt_button;
  if (formData.bubble_text !== undefined)
    updateData.bubble_text = formData.bubble_text;
  if (formData.start_message !== undefined)
    updateData.start_message = formData.start_message;

  // Avatar URLs (use new uploaded URL or existing)
  if (newHeaderAvatarUrl) {
    updateData.header_avatar_url = newHeaderAvatarUrl;
  }
  if (newChatAvatarUrl) {
    updateData.chat_avatar_url = newChatAvatarUrl;
  }

  // Persist webscrap config onto the attached knowledge base —
  // skipped when the user just switched to a different existing
  // knowledge base via the toggle, since that's a shared resource
  // this form doesn't own. Non-fatal: the chat agent update below
  // isn't blocked by this failing; the warning is reported with the rest.
  let webscrapWarning: string | undefined;
  if (!formData.existing_knowledge_id && knowledge?.id) {
    webscrapWarning = await saveWebscrapConfig(knowledge.id, formData, t);
  }

  // Update the chat agent
  await updateChatAgent(chatAgent.id, updateData);

  queryClient.invalidateQueries({ queryKey: ['chat-agents'] });
  setIsAgentCreated(true);
  setIsKnowledgeCreated(true);

  // Upload new knowledge files if any were added — skip if we just
  // detached or switched knowledge bases (see knowledgeForFiles). Flush any
  // staged deletions in parallel, mirroring AgentForm/KnowledgeForm.
  // Settled (not `all`) so a failed upload can't suppress the
  // report of deletions that already completed.
  const [editUploadResult, editDeletionResult] = await Promise.allSettled([
    knowledgeFilesData.length > 0 && knowledgeForFiles
      ? uploadKnowledgeFiles(knowledgeFilesData, knowledgeForFiles)
      : Promise.resolve(),
    executeStagedDeletions(),
  ]);
  setIsFilesUploaded(true);

  // Each part is a complete, independently-translated sentence, joined
  // with a space so translated output stays grammatically valid in every
  // locale.
  const editParts: string[] = webscrapWarning ? [webscrapWarning] : [];
  if (
    editUploadResult.status === 'fulfilled' &&
    editUploadResult.value &&
    'failed' in editUploadResult.value &&
    editUploadResult.value.failed.length > 0
  ) {
    editParts.push(
      t('Could not upload: {{files}}', {
        files: editUploadResult.value.failed.join(', '),
      })
    );
  }
  if (editDeletionResult.status === 'fulfilled') {
    if (editDeletionResult.value.failed.length > 0) {
      editParts.push(
        t('Could not delete: {{files}}', {
          files: editDeletionResult.value.failed.join(', '),
        })
      );
    }
  } else {
    editParts.push(
      editDeletionResult.reason instanceof Error
        ? editDeletionResult.reason.message
        : t('Some staged file deletions could not be completed.')
    );
  }

  if (editParts.length > 0) {
    displayMessage(
      [t('Chat agent updated successfully.'), ...editParts].join(' '),
      'warning',
      6000
    );
  } else {
    displayMessage(t('Chat agent updated successfully.'), 'success');
  }

  // Invalidate caches so the table reflects changes
  queryClient.invalidateQueries({ queryKey: ['chat-agents'] });
  queryClient.invalidateQueries({ queryKey: ['knowledges'] });
  queryClient.invalidateQueries({
    queryKey: ['knowledges-with-attachment'],
  });
};
